import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Search, Heart, Plane, User, Instagram, MapPin } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import RegistrationPrompt from "./RegistrationPrompt";

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Navigation is now available to all users (including unregistered/free tier)
  // Individual features handle their own permission checks via usePermissions hook

  const navItems = [
    { path: "/app/chat", icon: MessageCircle, label: "Chat" },
    { path: "/app/explore", icon: Search, label: "Explore" },
    { path: "/app/saved", icon: Heart, label: "Saved" },
    { path: "/app/featured", icon: Instagram, label: "", isProminent: true },
    { path: "/app/plan", icon: MapPin, label: "Plan" },
    { path: "/app/services", icon: "tuk-tuk", label: "Services" },
    { path: "/app/profile", icon: User, label: "Profile" }
  ];

  const handleNavClick = (path: string, e: React.MouseEvent) => {
    // If user is not logged in and trying to access profile, show auth popup
    if (path === "/app/profile" && !authState.user) {
      e.preventDefault();
      setShowAuthModal(true);
      return;
    }
    // Otherwise, navigate normally
    navigate(path);
  };

  return (
    <>
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex items-center justify-between px-2 relative">
          {/* Left side buttons */}
          <div className="flex justify-around flex-1">
            {navItems.slice(0, 3).map(({ path, icon, label }) => {
              const isActive = location.pathname === path;
              const isTukTuk = icon === "tuk-tuk";
              
              return (
                <button
                  key={path}
                  onClick={(e) => handleNavClick(path, e)}
                  className={`flex flex-col items-center py-2 px-1 transition-colors duration-200 ${
                    isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {isTukTuk ? (
                    <img 
                      src="/tuk-tuk.png" 
                      alt="Tuk Tuk" 
                      className={`w-5 h-5 ${isActive ? 'opacity-100' : 'opacity-60'}`}
                    />
                  ) : (
                    React.createElement(icon, { 
                      size: 20, 
                      strokeWidth: 2,
                      className: isActive ? 'fill-current' : '' 
                    })
                  )}
                  {label && <span className="text-xs mt-1 font-medium">{label}</span>}
                </button>
              );
            })}
          </div>

          {/* Center Featured button */}
          {(() => {
            const featuredItem = navItems[3];
            const isActive = location.pathname === featuredItem.path;
            return (
              <button
                key={featuredItem.path}
                onClick={(e) => handleNavClick(featuredItem.path, e)}
                className="flex flex-col items-center py-2 px-3 transition-colors duration-200"
              >
                <Instagram 
                  size={32} 
                  strokeWidth={1.5}
                  className={isActive ? 'fill-current text-purple-600' : 'text-purple-600'} 
                  style={{ color: '#663399' }}
                />
              </button>
            );
          })()}

          {/* Right side buttons */}
          <div className="flex justify-around flex-1">
            {navItems.slice(4).map(({ path, icon, label }) => {
              const isActive = location.pathname === path;
              const isTukTuk = icon === "tuk-tuk";
              
              return (
                <button
                  key={path}
                  onClick={(e) => handleNavClick(path, e)}
                  className={`flex flex-col items-center py-2 px-1 transition-colors duration-200 ${
                    isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {isTukTuk ? (
                    <img 
                      src="/tuk-tuk.png" 
                      alt="Tuk Tuk" 
                      className={`w-5 h-5 ${isActive ? 'opacity-100' : 'opacity-60'}`}
                    />
                  ) : (
                    React.createElement(icon, { 
                      size: 20, 
                      strokeWidth: 2,
                      className: isActive ? 'fill-current' : '' 
                    })
                  )}
                  {label && <span className="text-xs mt-1 font-medium">{label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <RegistrationPrompt
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        variant="google_auth"
        trigger="profile"
      />
    </>
  );
}
